"use client";

import Image from "next/image";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { DOOR_LABEL, type Door } from "../_content/card";
import { Icon } from "../_family/Icon";
import { useReducedMotion } from "../_family/useReducedMotion";
import type { DoorCode } from "./content";
import { Tokens } from "./Tokens";
import { TRAVELLER } from "./colour";
import { DOOR_SAYS } from "./words";

/** What comes back through every door: the first of the turn's events, in the order they arrive. */
export const COMES_BACK = [
  { type: "conversation.status", kind: "msg" },
  { type: "message.delta", kind: "msg" },
  { type: "task.started", kind: "work" },
  { type: "interaction.created", kind: "wait" },
] as const;

const REPLY = "On it. Looking for the next flight to Mumbai.";
/** How long each door stays while they take turns (the tab's bar fills in the same time: landing.css). */
const DOOR_MS = 4200;

/** A door picked part way through its reply. */
class Stopped extends Error {}

function Said({ text }: { text: string }) {
  return text
    .split("`")
    .map((part, i) =>
      i % 2 ? <code key={i}>{part}</code> : <Fragment key={i}>{part}</Fragment>
    );
}

function Face() {
  return (
    <span className="face">
      <Image
        src="/developers/family/face-bash.webp"
        width={320}
        height={320}
        alt=""
        unoptimized
      />
    </span>
  );
}

/**
 * Five doors, the same ask: the tabs take turns while the section is in view, until you pick one; beside the
 * code, the reply and the first events, the same whichever door.
 */
export function Doors({ codes }: { codes: DoorCode[] }) {
  const [door, setDoor] = useState<Door>("ts");
  const [auto, setAuto] = useState(true);
  const [seen, setSeen] = useState(false);
  const [held, setHeld] = useState(false);
  const still = useReducedMotion();
  const [words, setWords] = useState(0);
  const [events, setEvents] = useState(0);
  const panel = useRef<HTMLDivElement>(null);
  const tabs = useRef<Array<HTMLButtonElement | null>>([]);
  const order = useMemo(() => codes.map((c) => c.door), [codes]);
  const code = codes.find((c) => c.door === door);
  const replyWords = REPLY.split(" ");

  // Watching: the tabs take turns only while the doors are on screen.
  useEffect(() => {
    const el = panel.current;
    if (!el) return;
    const io = new IntersectionObserver(([en]) => setSeen(en.isIntersecting), {
      threshold: 0.4,
    });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // The next door, after a while: never while a pointer rests on the doors, and never again once one is
  // picked or a keyboard reaches the tabs.
  const cycling = auto && seen && !still && !held;
  const hold = {
    onMouseEnter: () => setHeld(true),
    onMouseLeave: () => setHeld(false),
  };
  useEffect(() => {
    if (!cycling) return;
    const t = setTimeout(() => {
      setDoor(order[(order.indexOf(door) + 1) % order.length]);
      setWords(0);
      setEvents(0);
    }, DOOR_MS);
    return () => clearTimeout(t);
  }, [cycling, door, order]);

  // Each door replies, word by word, its events arriving as they would.
  useEffect(() => {
    let live = true;
    const wait = (ms: number) =>
      new Promise<void>((resolve, reject) =>
        setTimeout(
          () => (live ? resolve() : reject(new Stopped())),
          still ? 0 : ms
        )
      );
    (async () => {
      await wait(350);
      setEvents(1);
      for (let i = 1; i <= replyWords.length; i++) {
        setWords(i);
        if (i === 1) setEvents(2);
        await wait(70);
      }
      await wait(250);
      setEvents(3);
      await wait(350);
      setEvents(4);
    })().catch((err) => {
      if (!(err instanceof Stopped)) throw err;
    });
    return () => {
      live = false;
    };
  }, [door, still, replyWords.length]);

  const pick = (d: Door, focus = false) => {
    setAuto(false);
    // A door opens with its reply not yet begun.
    if (d !== door) {
      setDoor(d);
      setWords(0);
      setEvents(0);
    }
    if (focus) tabs.current[order.indexOf(d)]?.focus();
  };

  return (
    <>
      <div
        className="chans rv"
        role="tablist"
        aria-label="Doors"
        {...hold}
        onFocus={() => setAuto(false)}
      >
        {order.map((d, i) => (
          <button
            key={d}
            type="button"
            role="tab"
            id={`vf-door-${d}`}
            aria-selected={d === door}
            aria-controls="vf-door-panel"
            tabIndex={d === door ? 0 : -1}
            className={d === door && cycling ? "auto" : undefined}
            ref={(el) => {
              tabs.current[i] = el;
            }}
            onClick={() => pick(d)}
            onKeyDown={(ev) => {
              if (ev.key !== "ArrowRight" && ev.key !== "ArrowLeft") return;
              ev.preventDefault();
              const step = ev.key === "ArrowRight" ? 1 : order.length - 1;
              pick(order[(i + step) % order.length], true);
            }}
          >
            <span className="ch-ic">
              <Icon name={DOOR_SAYS[d].icon} />
            </span>
            {DOOR_LABEL[d]}
          </button>
        ))}
      </div>
      <div
        className="doors rv d1"
        id="vf-door-panel"
        ref={panel}
        role="tabpanel"
        aria-labelledby={`vf-door-${door}`}
        {...hold}
      >
        <div className="dwin">
          <div className="edwin-bar">
            <span className="lights">
              <i />
              <i />
              <i />
            </span>
            <b>{DOOR_SAYS[door].file}</b>
          </div>
          <div key={door} className={still ? "dcode" : "dcode swap"}>
            {code && <Tokens tokens={code.tokens} />}
          </div>
          <p className="dcap">
            <Said text={DOOR_SAYS[door].says} />
          </p>
        </div>
        <div className="comes">
          <h3>What comes back</h3>
          <div className="card-w">
            <div className="wh-head">
              <Face />
              <div>
                <b>Bash</b>
                <small>for {TRAVELLER}</small>
              </div>
            </div>
            <div className="wh-reply">
              <Face />
              <p>{replyWords.slice(0, words).join(" ")}</p>
            </div>
          </div>
          <ul className="evs">
            {COMES_BACK.map((ev, i) => (
              <li
                key={ev.type}
                className={i < events ? `in ev-${ev.kind}` : `ev-${ev.kind}`}
              >
                <i />
                {ev.type}
              </li>
            ))}
          </ul>
          <small>
            The same reply and the same event names, whichever door you use.
          </small>
        </div>
      </div>
    </>
  );
}
